import { FunctionalComponent, JSX, h } from '@stencil/core';
import { store } from 'app/shared/store/store';
import { getContent, replacePlaceholders } from 'app/shared/utilities/helper-functions';
import { ErrorType, FeatureKey, ProductCatalogueProduct, TechnologyTypeProps } from 'app/shared/models/index';
import { NbnProductsResponse } from 'app/nbn/models/nbn-products-response.model';
import { authenticationService, customerProfileService, featureService, tealiumAnalyticService } from 'app/shared/services/index';

export const showNbnSqMessage = (
  nbnProductDetails: NbnProductsResponse,
  address: string,
  isGuidedFlow: boolean,
  isNbnMoversCrossSell = false
): JSX.Element => {
  const delayMessage = store.get('delayMessage');

  if (isGuidedFlow) {
    return null;
  }

  return (
    <div>{renderTechTypeMessage(nbnProductDetails, address, false, delayMessage, false, null, isGuidedFlow, isNbnMoversCrossSell)}</div>
  );
};

const getTechnologyTypeString = (technologyType: string): string => {
  const type = technologyType ? technologyType.toUpperCase() : '';
  switch (type) {
    case 'FIBRE':
      return 'Fibre to the Premises';
    case 'FTTN':
      return 'Fibre to the Node';
    case 'FTTC':
      return 'Fibre to the Curb';
    case 'FTTB':
      return 'Fibre to the Building';
    case 'HFC':
      return 'Hybrid Fibre Coaxial';
    case 'WIRELESS':
      return 'Fixed Wireless';
    case 'LTSS':
      return 'Satellite';
    default:
      return undefined;
  }
};

export const NbnTechnologyTypeMessage: FunctionalComponent<TechnologyTypeProps> = ({
  technologyType,
  noService,
  isLoading,
  delayMessage,
  noHfcService,
  isGuidedFlow = false,
  eligibleFibreUpgrade = false,
  cheapestEligiblePlan,
  isNbnMoversCrossSell = false
}) => {
  const styledAs = isGuidedFlow ? 'xl' : 'lg';

  if (noHfcService === true && !isNbnMoversCrossSell) {
    const path = window.location.origin + window.location.pathname.replace(/\/+$/, '');
    const errText = `Unfortunately, because your address has an nbn HFC connection type, we’re not currently able to connect you, due to a global supply shortage experienced by nbn co. More information can be found in the section below called `;
    return (
      <agl-ds-p styledAs={styledAs}>
        <agl-ds-text appearance="error">
          {errText}
          <agl-ds-link
            appearance="default"
            styled-as="lg"
            open-new-window="false"
            href={`${path}#hfc`}
            sr-context="Non-serviceable HFC addresses"
          >
            Non-serviceable HFC addresses
          </agl-ds-link>
          .
        </agl-ds-text>
      </agl-ds-p>
    );
  }

  if (noService === true && !isNbnMoversCrossSell) {
    return (
      <agl-ds-p styledAs={styledAs}>
        <agl-ds-text appearance="error">Looks like we don't provide internet service at above address.</agl-ds-text>
      </agl-ds-p>
    );
  }

  if (isLoading && delayMessage && !isNbnMoversCrossSell) {
    return (
      <agl-ds-p styledAs={styledAs} appearance="muted">
        {delayMessage}
      </agl-ds-p>
    );
  }

  if (!technologyType && !isNbnMoversCrossSell) {
    return (
      <agl-ds-p styledAs={styledAs} appearance="muted">
        See plans available at your address
      </agl-ds-p>
    );
  }

  const key = eligibleFibreUpgrade ? 'sqResponseMessageFibreUpgrade' : 'sqResponseMessage';
  let message = getContent(key);
  if (!message) {
    return null;
  }

  const replacements = {
    technologyType,
    technologyTypeString: getTechnologyTypeString(technologyType),
    cheapestEligiblePlan
  };

  message = replacePlaceholders(message, replacements);

  const nbnMoversTechTypes = ['FTTP', 'FTTN', 'HFC', 'FIBRE', 'FTTC', 'FTTB', 'FW', 'WIRELESS'];
  const validTechType = nbnMoversTechTypes.includes(technologyType.toUpperCase());
  const isMoversFlow = sessionStorage.getItem('appConfig') === 'movers';
  const featureFlag = featureService.getFeatureStatus(FeatureKey.NBN_MOVERS_ENABLED);
  const customerProfile = customerProfileService.getCustomerProfile();
  const isExistingUser = !customerProfile?.isNewCustomer || authenticationService.loggedIn;
  const isFibreUpgrade = eligibleFibreUpgrade === 'FIBRE';

  const mismatchedTechTypeMessage = `
    <div>
      <agl-ds-text>
        Can't see your current plan? This could mean the technology type at your new address is different to your old address.&nbsp;
      </agl-ds-text>
      <agl-ds-link href="https://www.agl.com.au/help-support/internet-mobile/network-and-coverages/nbn-connection-types">
        Learn more about technology types
      </agl-ds-link>
    </div>
  `;

  if (isNbnMoversCrossSell) {
    return <moving-fibre-upgrade-message-info message={message} />;
  } else if (validTechType && isExistingUser && isMoversFlow && featureFlag && !isFibreUpgrade) {
    return (
      <div>
        <message-wrapper id="sq-message" message={message} />
        <message-wrapper id="sq-message" message={mismatchedTechTypeMessage} />
      </div>
    );
  }
  return <message-wrapper id="sq-message" message={message} />;
};

export const getCheapestFibreUpgradePlan = (productDetails: NbnProductsResponse): string => {
  const eligiblePlans = productDetails.products.filter((p) => p?.fibreUpgrade === true);
  let cheapestEligiblePlan: ProductCatalogueProduct = null;
  if (eligiblePlans.length > 0) {
    cheapestEligiblePlan = eligiblePlans.sort((p1, p2) => parseInt(p1.price, 10) - parseInt(p2.price, 10))[0];
    return cheapestEligiblePlan.content.productName;
  }

  return '<i>No Fibre Connect plan found</i>';
};

export const renderTechTypeMessage = (
  productDetails: NbnProductsResponse,
  selectedAddressString: string,
  isLoading: boolean,
  delayMessage: string,
  hasError: boolean,
  errorType: ErrorType,
  isGuidedFlow = false,
  isNbnMoversCrossSell = false
): JSX.Element => {
  if (hasError && selectedAddressString) {
    tealiumAnalyticService.sendAnalyticsError(
      '[BCP][Search][Address][Error]',
      null,
      `We don't provide internet service at above address`,
      'Inline'
    );
    if (errorType === 'hfcServiceNotAvailable') {
      return <NbnTechnologyTypeMessage noHfcService />;
    }
    return <NbnTechnologyTypeMessage noService />;
  } else if (isLoading && delayMessage) {
    return <NbnTechnologyTypeMessage isLoading={isLoading} delayMessage={delayMessage} />;
  } else if (!productDetails?.serviceQualification) {
    return <NbnTechnologyTypeMessage />;
  }
  const eligibleFibreUpgrade = productDetails.serviceQualification.nbn.alternativeServiceType;

  const cheapestEligiblePlan = getCheapestFibreUpgradePlan(productDetails);
  return (
    <NbnTechnologyTypeMessage
      technologyType={productDetails.serviceQualification.nbn.serviceType}
      isGuidedFlow={isGuidedFlow}
      eligibleFibreUpgrade={eligibleFibreUpgrade}
      cheapestEligiblePlan={cheapestEligiblePlan}
      isNbnMoversCrossSell={isNbnMoversCrossSell}
    />
  );
};
